import React, { useEffect, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { getLogin, checkSession } from "../slice/AuthSlice";
import { useNavigate } from "react-router-dom";
import Loader from "./Loader/Loader";
import LoginPage from "../pages/LoginPage";
import { consumeSignedOutFlag } from "../utils/signedOutFlag";
import { consumeReturnTo } from "../utils/returnTo";
import { sessionEndedReason } from "../utils/idleSession";
import { MOBILE_MEDIA_QUERY } from "../hooks/useIsMobile";

const AuthRedirect = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // After an explicit sign out, do NOT auto-launch the Salesforce OAuth
  // flow: an alive Salesforce session would silently sign the user right
  // back in, making Sign out look like a page refresh (production bug
  // 2026-07-16). Show the explicit sign-in page instead. Initialized
  // lazily so the flag is consumed exactly once per landing.
  // The same applies after WHG Docs signs someone out for inactivity: they
  // sign back in themselves.
  const [showLoginPage] = useState(
    () => consumeSignedOutFlag() || Boolean(sessionEndedReason())
  );

  const handleAuth = useCallback(async () => {
    try {
      // This component is only used for production environments (dev.dms.com, staging, etc.)
      // For localhost, we use the LoginPage component instead
      console.log('Production environment detected - using direct redirect flow');
      
      const response = await dispatch(checkSession()).unwrap();
      
      if (response?.status === 401) {
        dispatch(getLogin());
      } else if (response?.status === 200) {
        // The Word panel's sign-in dialog sent us here; go back and finish
        // connecting instead of landing on the app.
        if (globalThis.sessionStorage?.getItem("whgdocs.wordAddin.connect") === "1") {
          navigate('/word-addin/connect', { replace: true });
          return;
        }
        // Back to the page the sign-in interrupted, when there was one
        // (a document link from the Salesforce intake console, say).
        const returnTo = consumeReturnTo();
        if (returnTo) {
          navigate(returnTo, { replace: true });
          return;
        }
        // Phones land on the mobile Home dashboard; desktop keeps the
        // historical Matters landing. Plain matchMedia (not the hook)
        // because this runs once inside an async callback.
        const isMobile =
          globalThis.window?.matchMedia?.(MOBILE_MEDIA_QUERY)?.matches;
        navigate(isMobile ? '/home' : '/matter', { replace: true });
      }
    } catch (error) {
      console.error("Authentication error:", error);
      dispatch(getLogin());
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!showLoginPage) {
      handleAuth();
    }
  }, [handleAuth, showLoginPage]);

  if (showLoginPage) {
    return <LoginPage />;
  }

  return <Loader />;
};

export default AuthRedirect;
